import { useState, useEffect } from 'react';
import agriwareLogo from '../../assets/agriware-logo.svg';
import logomark from '../../assets/logomark.svg';

// Shared front door for every Agriware product.
//
// Deliberately identical across Ops and Workshop — only the slides, the
// product label and the form inside it differ. Anything structural changed
// here should be changed in the other product's copy at the same time.

const SLIDE_MS = 5000;

export default function AuthShell({ productLabel, slides, children }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!slides || slides.length < 2) return;
    const timer = setInterval(() => setCurrent((p) => (p + 1) % slides.length), SLIDE_MS);
    return () => clearInterval(timer);
  }, [slides]);

  const slide = slides && slides.length ? slides[current] : null;
  const SlideIcon = slide ? slide.icon : null;

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      <style>{`
        @keyframes agw-slide-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: none; }
        }
        .agw-auth-input {
          width: 100%;
          box-sizing: border-box;
          padding: 11px 13px;
          font-size: 14px;
          font-family: inherit;
          color: var(--color-text-primary, #2C2C2A);
          background: #fff;
          border: 0.5px solid var(--color-border-medium, #D3D1C7);
          border-radius: var(--radius-md, 6px);
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .agw-auth-input:focus {
          border-color: var(--color-brand, #E8610A);
          box-shadow: 0 0 0 3px rgba(232, 97, 10, 0.12);
        }
      `}</style>

      <div
        style={{
          position: 'relative',
          width: '40%',
          minWidth: 380,
          background: '#141B24',
          padding: '48px 52px',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {/* Every slide is stacked and faded, so one image dissolves into the
            next instead of cutting. */}
        {slides && slides.map((s, i) => (
          s.image ? (
            <div
              key={i}
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(rgba(0,0,0,0.58), rgba(0,0,0,0.58)), url(${s.image}) center/cover`,
                opacity: i === current ? 1 : 0,
                transition: 'opacity 900ms ease-in-out',
                pointerEvents: 'none',
              }}
            />
          ) : null
        ))}
        <div style={{ position: 'relative', marginBottom: 56 }}>
          <img src={agriwareLogo} alt="Agriware" style={{ height: 30, width: 'auto', display: 'block' }} />
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.1em', marginTop: 8 }}>
            {productLabel}
          </div>
        </div>

        {slide && (
          <div key={current} style={{ position: 'relative', flex: 1, animation: 'agw-slide-in 900ms ease-out' }}>
            {SlideIcon && (
              <div style={{ width: 52, height: 52, borderRadius: 12, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
                <SlideIcon size={26} color="white" stroke={1.5} />
              </div>
            )}
            <div style={{ fontSize: 26, fontWeight: 400, color: '#fff', lineHeight: 1.35, marginBottom: 14, maxWidth: 400 }}>
              {slide.title}
            </div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, marginBottom: 28, maxWidth: 400 }}>
              {slide.body}
            </div>
            {slide.stats && (
              <div style={{ display: 'flex', gap: 12 }}>
                {slide.stats.map((stat, i) => (
                  <div key={i} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '12px 18px' }}>
                    <div style={{ fontSize: 22, fontWeight: 400, color: '#fff' }}>{stat.val}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>{stat.lbl}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {slides && slides.length > 1 && (
          <div style={{ position: 'relative', display: 'flex', gap: 5, marginTop: 28 }}>
            {slides.map((_, i) => (
              <div
                key={i}
                onClick={() => setCurrent(i)}
                style={{
                  width: i === current ? 16 : 5,
                  height: 5,
                  borderRadius: 3,
                  background: i === current ? '#fff' : 'rgba(255,255,255,0.3)',
                  cursor: 'pointer',
                  transition: 'width 0.3s, background 0.3s',
                }}
              />
            ))}
          </div>
        )}

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20 }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>agriware.uk</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>&copy; {new Date().getFullYear()} Agriware Ltd</div>
        </div>
      </div>

      {/* Right — the form over a tiled watermark, so the space reads as ours
          rather than as empty. */}
      <div
        style={{
          position: 'relative',
          flex: 1,
          background: 'var(--color-bg-secondary, #FAFAF8)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px 80px',
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: '-40%',
            backgroundImage: `url(${logomark})`,
            backgroundRepeat: 'repeat',
            backgroundSize: '92px auto',
            transform: 'rotate(-45deg)',
            transformOrigin: 'center',
            opacity: 0.1,
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: 400,
            background: 'var(--color-bg-primary, #fff)',
            border: '0.5px solid var(--color-border-light, #E8E6DE)',
            borderRadius: 'var(--radius-lg, 12px)',
            padding: '36px 34px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            maxHeight: 'calc(100vh - 80px)',
            overflowY: 'auto',
            // The signup form is taller than the login, so the card scrolls
            // rather than being clipped by the panel's overflow: hidden.
            maxHeight: 'calc(100vh - 80px)',
            overflowY: 'auto',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
